import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import API from 'utils/api'
import config from 'config'
import { buildUrl } from 'utils/helpers'
import { showErrorMessage } from 'store/ui/actions'

export const fetchAllCameras = createAsyncThunk(
  'camerasView/fetchAll',
  async (
    { page = 1, limit = 50, search = '' } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const normalizedSearch = search.trim()
      const { devices, meta } = await API.request(
        buildUrl('/devices', {
          device_type: 'camera',
          is_installed: true,
          include_linked_cameras: true,
          ...(normalizedSearch ? { search: normalizedSearch } : {}),
          page,
          limit,
          sort_by: 'address',
          order: 'desc',
        }),
        {},
        `${config.redirectUri}/api/v2`
      )
      return { devices, meta, page }
    } catch (err) {
      try {
        const { message } = await err.json()
        dispatch(showErrorMessage(message))
        return rejectWithValue(message)
      } catch {
        dispatch(showErrorMessage('Failed to fetch cameras'))
        return rejectWithValue('Failed to fetch cameras')
      }
    }
  }
)

// Parse address string into structured parts
// Format: "Страна, Регион, Округ, Город, улица, д. XX[кN][сN], п. N"
export function parseAddress(address) {
  if (!address) return null

  const result = { city: '', street: '', house: '', entrance: '', raw: address }

  // Extract entrance: ", п. N" at the end
  const entranceMatch = address.match(/, п\. (\d+)/)
  if (entranceMatch) {
    const [, entrance] = entranceMatch
    result.entrance = entrance
  }

  // Split by ", д. " to separate street part from house part
  const houseSplit = address.split(/, д\. /)
  if (houseSplit.length < 2) return result

  // House number: everything after "д. " up to ", п." or end
  const housePart = houseSplit[1]
  result.house = housePart.replace(/, п\. \d+.*$/, '').trim()

  // Street part: everything before ", д."
  const streetPart = houseSplit[0]
  const segments = streetPart
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean)
  const countryLike = /^(россия|russia|türkiye|turkey)$/i

  // Last segment = street name, previous one = city (when available)
  if (segments.length >= 3) {
    result.street = segments[segments.length - 1]
    result.city = segments[segments.length - 2]
  } else if (segments.length === 2) {
    const [cityCandidate, streetCandidate] = segments
    result.street = streetCandidate
    if (!countryLike.test(cityCandidate)) {
      result.city = cityCandidate
    }
  } else {
    result.street = streetPart.trim()
  }

  return result
}

// Build group key from parsed address (street + house)
function buildGroupKey(parsed) {
  if (!parsed || !parsed.street) return null
  if (!parsed.house) return parsed.street
  return `${parsed.street}, д. ${parsed.house}`
}

// Extract entrance number for sorting
function getEntranceNumber(parsed) {
  if (!parsed || !parsed.entrance) return 999
  return parseInt(parsed.entrance, 10) || 999
}

function isIntercomLinked(camera) {
  return Boolean(camera?.is_intercom_linked)
}

const initialState = {
  list: [],
  total: 0,
  totalPages: 0,
  page: 1,
  currentRequestId: null,
  limit: 50,
  filters: {
    search: '',
    status: 'all',
  },
  expandedGroups: {},
  pending: false,
  error: null,
}

export const camerasViewSlice = createSlice({
  name: 'camerasView',
  initialState,
  reducers: {
    setFilter: (state, { payload }) => {
      state.filters = { ...state.filters, ...payload }
    },
    toggleGroup: (state, { payload: addressKey }) => {
      const isExpanded = state.expandedGroups[addressKey] !== false
      state.expandedGroups[addressKey] = !isExpanded
    },
    clearCamerasView: () => initialState,
  },
  extraReducers: {
    [fetchAllCameras.pending]: (state, { meta }) => {
      state.pending = true
      state.error = null
      state.currentRequestId = meta.requestId
    },
    [fetchAllCameras.fulfilled]: (state, { payload, meta }) => {
      if (state.currentRequestId && state.currentRequestId !== meta.requestId) {
        return
      }
      const cameras = (payload.devices || []).filter((d) => d.type === 'camera')
      if (payload.page === 1) {
        state.list = cameras
      } else {
        state.list = [...state.list, ...cameras]
      }
      state.total = payload.meta?.total || cameras.length
      state.totalPages = payload.meta?.total_pages || 1
      state.page = payload.page
      state.pending = false
      state.currentRequestId = null
    },
    [fetchAllCameras.rejected]: (state, { payload, meta }) => {
      if (state.currentRequestId && state.currentRequestId !== meta.requestId) {
        return
      }
      state.pending = false
      state.error = payload
      state.currentRequestId = null
    },
  },
})

export const { setFilter, toggleGroup, clearCamerasView } =
  camerasViewSlice.actions

// Selectors
const selectCamerasViewState = (state) => state.camerasView

export const selectCamerasList = createSelector(
  selectCamerasViewState,
  (cv) => cv.list
)

export const selectFilters = createSelector(
  selectCamerasViewState,
  (cv) => cv.filters
)

export const selectFilteredCameras = createSelector(
  [selectCamerasList, selectFilters],
  (list, filters) => {
    let filtered = list

    if (filters.status === 'online') {
      filtered = filtered.filter((c) => c.is_online)
    } else if (filters.status === 'offline') {
      filtered = filtered.filter((c) => !c.is_online)
    }

    return filtered
  }
)

// Detect if all cameras are in the same city
export const selectIsSingleCity = createSelector(selectCamerasList, (list) => {
  const cities = new Set()
  list.forEach((c) => {
    const parsed = parseAddress(c.short_address)
    if (parsed?.city) cities.add(parsed.city)
  })
  return cities.size <= 1
})

export const selectGroupedByAddress = createSelector(
  [selectFilteredCameras, selectIsSingleCity],
  (cameras, isSingleCity) => {
    const gateGroups = {}
    const houseGroups = {}

    cameras.forEach((camera) => {
      if (!camera.short_address) return

      const parsed = parseAddress(camera.short_address)
      const enriched = { ...camera, parsedAddr: parsed }

      if (camera.address_type === 'gate') {
        const key = buildGroupKey(parsed) || camera.short_address
        if (!gateGroups[key]) gateGroups[key] = []
        gateGroups[key].push(enriched)
      } else {
        const key = buildGroupKey(parsed) || camera.short_address
        if (!houseGroups[key]) houseGroups[key] = []
        houseGroups[key].push(enriched)
      }
    })

    // Sort within address: intercom-linked cameras first, then by entrance
    const sortByEntrance = (a, b) => {
      const linkedDiff =
        Number(isIntercomLinked(b)) - Number(isIntercomLinked(a))
      if (linkedDiff !== 0) return linkedDiff

      const ea = getEntranceNumber(a.parsedAddr)
      const eb = getEntranceNumber(b.parsedAddr)
      if (ea !== eb) return ea - eb

      return (a.serial_number || '').localeCompare(b.serial_number || '')
    }

    const result = []

    // Gates first
    Object.keys(gateGroups)
      .sort()
      .forEach((key) => {
        result.push({
          key,
          type: 'gate',
          cameras: gateGroups[key].sort(sortByEntrance),
          city: isSingleCity ? null : gateGroups[key][0]?.parsedAddr?.city,
        })
      })

    // Then houses
    Object.keys(houseGroups)
      .sort()
      .forEach((key) => {
        result.push({
          key,
          type: 'house',
          cameras: houseGroups[key].sort(sortByEntrance),
          city: isSingleCity ? null : houseGroups[key][0]?.parsedAddr?.city,
        })
      })

    return result
  }
)

const selectInstalledCameras = createSelector(selectCamerasList, (list) =>
  list.filter((c) => c.short_address)
)

export const selectInstalledCount = createSelector(
  selectInstalledCameras,
  (list) => list.length
)

export const selectTotalInstalledCount = createSelector(
  [selectCamerasViewState, selectInstalledCount],
  (cv, installedCount) => cv.total || installedCount
)

export const selectOnlineCount = createSelector(
  selectInstalledCameras,
  (list) => list.filter((c) => c.is_online).length
)

export const selectOfflineCount = createSelector(
  selectInstalledCameras,
  (list) => list.filter((c) => !c.is_online).length
)

export const selectPagination = createSelector(
  selectCamerasViewState,
  (cv) => ({
    page: cv.page,
    totalPages: cv.totalPages,
    total: cv.total,
    pending: cv.pending,
  })
)

export const selectExpandedGroups = createSelector(
  selectCamerasViewState,
  (cv) => cv.expandedGroups
)

export default camerasViewSlice.reducer
